import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ServerCog,
  RefreshCw,
  Play,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  FlaskConical,
} from 'lucide-react';
import { apiService } from '@/core/api/service';
import { PageHeader } from '@/components/PageHeader';
import { SkeletonStats } from '@/components/SkeletonStats';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { StagingOverview, StagingSyncRun } from '@/core/models';

export const Route = createFileRoute('/staging')({
  head: () => ({
    meta: [{ title: 'Staging · Replya Internal Backoffice' }],
  }),
  component: StagingPage,
});

const REFRESH_INTERVAL = 15000;

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
}

function totalDocs(run: StagingSyncRun): number {
  return run.stats.reduce((acc, s) => acc + (s.docs ?? 0), 0);
}

function QueryError({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-5 py-4 text-sm text-destructive flex items-center gap-3">
      <AlertCircle className="h-5 w-5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold">Errore durante il caricamento</p>
        <p className="text-destructive/80 break-words">{error.message || 'Riprova tra qualche istante.'}</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry} className="rounded-xl shrink-0">
        Riprova
      </Button>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string | number | undefined }) {
  return (
    <Card className="rounded-2xl border bg-surface">
      <CardContent className="p-5">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="mt-2 text-3xl font-bold tracking-tight text-foreground break-words">
          {value ?? '-'}
        </p>
      </CardContent>
    </Card>
  );
}

function SyncRunStatusBadge({ status }: { status: StagingSyncRun['status'] }) {
  if (status === 'running') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> In corso
      </Badge>
    );
  }
  if (status === 'completed') {
    return (
      <Badge variant="secondary" className="gap-1 text-emerald-500">
        <CheckCircle2 className="h-3 w-3" /> Completato
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1 text-destructive">
      <XCircle className="h-3 w-3" /> Fallito
    </Badge>
  );
}

function StagingPage() {
  const queryClient = useQueryClient();
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [includeMainDb, setIncludeMainDb] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [tenantToSync, setTenantToSync] = useState<string | null>(null);
  const [summaryTenant, setSummaryTenant] = useState<string>('');

  const overviewQuery = useQuery<StagingOverview, Error>({
    queryKey: ['stagingOverview'],
    queryFn: () => apiService.getStagingOverview(),
    refetchInterval: REFRESH_INTERVAL,
  });

  const syncRunsQuery = useQuery<StagingSyncRun[], Error>({
    queryKey: ['stagingSyncRuns'],
    queryFn: () => apiService.getStagingSyncRuns(20),
    refetchInterval: REFRESH_INTERVAL,
  });

  const overview = overviewQuery.data;
  const syncRuns = syncRunsQuery.data ?? [];
  const tenants = overview?.tenants ?? [];
  const effectiveSummaryTenant = summaryTenant || tenants[0]?.tenantId || '';

  const summaryQuery = useQuery({
    queryKey: ['stagingTenantSummary', effectiveSummaryTenant],
    queryFn: () => apiService.getStagingTenantSummary(effectiveSummaryTenant, 7),
    enabled: !!effectiveSummaryTenant,
  });

  const syncMutation = useMutation({
    mutationFn: (req: { tenants?: string[]; includeMainDb?: boolean; dryRun: boolean }) =>
      apiService.syncStaging(req),
    onSuccess: (run, req) => {
      const docs = totalDocs(run);
      toast.success(
        req.dryRun
          ? `Dry run completato: ${docs} documenti verrebbero copiati`
          : `Sync completato: ${docs} documenti copiati`,
      );
      queryClient.invalidateQueries({ queryKey: ['stagingOverview'] });
      queryClient.invalidateQueries({ queryKey: ['stagingSyncRuns'] });
    },
    onError: (err: Error) => toast.error(`Sync fallito: ${err.message}`),
  });

  const lastRun = syncRuns[0];
  const isOnline = overviewQuery.isSuccess && !!overview?.syncEnabled;

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Staging"
        subtitle="Ambiente di test isolato dalla produzione — dati su DB replya_staging_*"
      >
        {overviewQuery.isError ? (
          <Badge variant="secondary" className="gap-1 text-destructive">
            <XCircle className="h-3 w-3" /> Offline
          </Badge>
        ) : isOnline ? (
          <Badge variant="secondary" className="gap-1 text-emerald-500">
            <CheckCircle2 className="h-3 w-3" /> Online
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1 text-amber-500">
            <AlertCircle className="h-3 w-3" /> Sync disabilitato
          </Badge>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            overviewQuery.refetch();
            syncRunsQuery.refetch();
          }}
          className="rounded-xl"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Aggiorna
        </Button>
        <Button
          size="sm"
          onClick={() => setSyncDialogOpen(true)}
          disabled={syncMutation.isPending}
          className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
        >
          <Play className="mr-2 h-4 w-4" />
          Allinea da produzione
        </Button>
      </PageHeader>

      {overviewQuery.isLoading && <SkeletonStats count={4} />}

      {overviewQuery.isError && (
        <QueryError error={overviewQuery.error} onRetry={overviewQuery.refetch} />
      )}

      {!overviewQuery.isLoading && !overviewQuery.isError && overview && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard label="NODE_ENV" value={overview.nodeEnv || '-'} />
          <MetricCard label="Prefisso DB tenant" value={overview.tenantDbPrefix || '-'} />
          <MetricCard
            label={`DB principale (${overview.mainDb.staging || 'staging'})`}
            value={`${overview.mainDb.stagingStats.docs} docs`}
          />
          <MetricCard
            label="Ultimo sync"
            value={
              lastRun
                ? `${formatDateTime(lastRun.startedAt)} · ${
                    lastRun.status === 'completed'
                      ? 'ok'
                      : lastRun.status === 'running'
                        ? 'in corso'
                        : 'fallito'
                  }`
                : 'Mai eseguito'
            }
          />
        </div>
      )}

      {/* Database tenant */}
      {!overviewQuery.isLoading && !overviewQuery.isError && (
        <Card className="rounded-2xl border bg-surface">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Database tenant</h3>
            {tenants.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun tenant trovato su staging.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead className="text-right">Doc produzione</TableHead>
                    <TableHead className="text-right">Doc staging</TableHead>
                    <TableHead className="text-right">Collection</TableHead>
                    <TableHead>Ultimo sync</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((t) => {
                    const outOfSync = t.prodDocs !== t.stagingDocs;
                    return (
                      <TableRow key={t.tenantId}>
                        <TableCell className="font-medium">{t.tenantId}</TableCell>
                        <TableCell className="text-right">{t.prodDocs}</TableCell>
                        <TableCell
                          className={`text-right ${outOfSync ? 'text-amber-500 font-semibold' : ''}`}
                        >
                          {t.stagingDocs}
                        </TableCell>
                        <TableCell className="text-right">{t.collections}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDateTime(t.lastSyncAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            title={`Sincronizza ${t.tenantId} da produzione`}
                            onClick={() => setTenantToSync(t.tenantId)}
                            disabled={syncMutation.isPending}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Analitiche staging */}
      {!overviewQuery.isLoading && !overviewQuery.isError && tenants.length > 0 && (
        <Card className="rounded-2xl border bg-surface">
          <CardContent className="p-5 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-semibold text-foreground">
                Analitiche staging (ultimi 7 giorni)
              </h3>
              <Select value={effectiveSummaryTenant} onValueChange={setSummaryTenant}>
                <SelectTrigger className="w-56 rounded-xl bg-surface-2 border-border">
                  <SelectValue placeholder="Seleziona tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.tenantId} value={t.tenantId}>
                      {t.tenantId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {summaryQuery.isLoading && <SkeletonStats count={4} />}
            {summaryQuery.isError && (
              <p className="text-sm text-muted-foreground">
                Analitiche non disponibili per questo tenant su staging
                {summaryQuery.error?.message ? ` — ${summaryQuery.error.message}` : ''}.
              </p>
            )}
            {summaryQuery.data && (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <MetricCard label="Richieste" value={summaryQuery.data.totalRequests} />
                <MetricCard label="Conversazioni" value={summaryQuery.data.totalConversations} />
                <MetricCard
                  label="Messaggi chat"
                  value={summaryQuery.data.totalChatMessages ?? '-'}
                />
                <MetricCard label="Errori" value={summaryQuery.data.totalErrors} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Storico sync */}
      <Card className="rounded-2xl border bg-surface">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Storico sync</h3>
          {syncRunsQuery.isLoading && <SkeletonStats count={2} />}
          {syncRunsQuery.isError && (
            <QueryError error={syncRunsQuery.error} onRetry={syncRunsQuery.refetch} />
          )}
          {!syncRunsQuery.isLoading && !syncRunsQuery.isError && syncRuns.length === 0 && (
            <p className="text-sm text-muted-foreground">Nessun sync eseguito finora.</p>
          )}
          <div className="space-y-2">
            {syncRuns.map((run) => (
              <div key={run._id} className="rounded-xl bg-surface-2 px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <ServerCog className="h-4 w-4 text-brand shrink-0" />
                  <SyncRunStatusBadge status={run.status} />
                  {run.dryRun && (
                    <Badge variant="secondary" className="gap-1 text-amber-500">
                      <FlaskConical className="h-3 w-3" /> Dry run
                    </Badge>
                  )}
                  {run.includeMainDb && <Badge variant="secondary">DB principale</Badge>}
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(run.startedAt)}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                  <span>
                    {run.requestedTenants.length > 0
                      ? `${run.requestedTenants.length} tenant`
                      : 'Tutti i tenant'}
                  </span>
                  <span>{totalDocs(run)} documenti</span>
                  <span>{run.stats.reduce((acc, s) => acc + (s.collections ?? 0), 0)} collection</span>
                </div>
                {run.error && <p className="mt-1 text-xs text-destructive break-words">{run.error}</p>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialog sync completo */}
      <AlertDialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Allinea staging da produzione</AlertDialogTitle>
            <AlertDialogDescription>
              Il sync è incrementale e non distruttivo: i documenti di produzione vengono copiati
              su staging in upsert, senza cancellare nulla. Puoi prima simulare l'operazione con
              un dry run.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="sync-include-main-db"
                checked={includeMainDb}
                onCheckedChange={(checked) => setIncludeMainDb(checked === true)}
              />
              <Label htmlFor="sync-include-main-db" className="text-sm">
                Includi DB principale (registry, short link)
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="sync-dry-run"
                checked={dryRun}
                onCheckedChange={(checked) => setDryRun(checked === true)}
              />
              <Label htmlFor="sync-dry-run" className="text-sm">
                Dry run (simula senza scrivere)
              </Label>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                syncMutation.mutate({
                  includeMainDb,
                  // dryRun va passato esplicitamente anche quando false:
                  // il backend defaulta a dry-run se il flag manca.
                  dryRun,
                })
              }
              className="bg-brand text-brand-foreground hover:bg-brand-glow"
            >
              Avvia sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog sync singolo tenant */}
      <AlertDialog open={!!tenantToSync} onOpenChange={(open) => !open && setTenantToSync(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sincronizzare "{tenantToSync}"?</AlertDialogTitle>
            <AlertDialogDescription>
              I dati del tenant "{tenantToSync}" verranno copiati da produzione a staging in
              upsert, senza cancellare documenti esistenti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                tenantToSync &&
                syncMutation.mutate({ tenants: [tenantToSync], dryRun: false })
              }
              className="bg-brand text-brand-foreground hover:bg-brand-glow"
            >
              Avvia sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
